import React, { useContext, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { SSlides, Controls, SReader } from "./PDFReader.styles";
import { SocketContext } from "../../context/SocketContext";
import { MediaContext } from "../../context/MediaContext";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/* It shows the presentation slides. We use react-pdf library to implement this functionality. */
const PDFReader = ({ filePath, isTeacher = false, expanded, setExpanded, downloadSlides = null }) => {
  const { teacherSlideNumber, sendSocketEvent } = useContext(SocketContext);
  const { slideNumber, setSlideNumber, isFollowing, setIsFollowing } = useContext(MediaContext);

  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(0.7);
  const [numPages, setNumPages] = useState(0);

  const isFirstPage = slideNumber === 1;
  const isLastPage = slideNumber === numPages;
  const firstPageClass = isFirstPage ? "disabled" : "clickable";
  const lastPageClass = isLastPage ? "disabled" : "clickable";
  const isMinZoom = scale < 0.6;
  const isMaxZoom = scale >= 2.0;
  const zoomOutClass = isMinZoom ? "disabled" : "clickable";
  const zoomInClass = isMaxZoom ? "disabled" : "clickable";
  const followClass = isFollowing ? "disabled" : "clickable";

  useEffect(() => {
    if (isFollowing) {
      setSlideNumber(teacherSlideNumber || 1);
    }
  }, [isFollowing, setSlideNumber, slideNumber, teacherSlideNumber]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setIsLoading(false);
  }

  const emitTeacherPage = (page) => {
    if (isTeacher) sendSocketEvent("teacher/slideNumber", { slideNumber: page });
  };

  const goToPreviousPage = () => {
    if (!isFirstPage) {
      setSlideNumber(slideNumber - 1);
      setIsFollowing(false);
      emitTeacherPage(slideNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (!isLastPage) {
      setSlideNumber(slideNumber + 1);
      setIsFollowing(false);
      emitTeacherPage(slideNumber + 1);
    }
  };

  const onPageChange = (e) => {
    setSlideNumber(Number(e.target.value));
    setIsFollowing(false);
    emitTeacherPage(Number(e.target.value));
  };

  const zoomOut = () => {
    if (!isMinZoom) setScale(scale - 0.1);
  };

  const zoomIn = () => {
    if (!isMaxZoom) setScale(scale + 0.1);
  };

  const onFollow = () => {
    setSlideNumber(teacherSlideNumber);
    setIsFollowing(true);
  };

  return (
    <SSlides>
      <SReader>
        {isLoading && (
          <div className="loader">
            <img src="https://react-pdf.org/images/logo.png" alt="Loading..." />
          </div>
        )}
        <Document file={filePath} onLoadSuccess={onDocumentLoadSuccess}>
          <Page className="d-flex justify-content-center" pageNumber={slideNumber || 1} scale={scale} />
        </Document>
      </SReader>
      <Controls>
        <div className="d-flex flex-row align-items-center">
          <i className={`fas fa-step-backward mx-2 ${firstPageClass}`} onClick={goToPreviousPage} />
          <input
            style={{ width: "2rem" }}
            onChange={onPageChange}
            type="number"
            value={slideNumber || 1}
            min={1}
            max={numPages || 1}
          />
          <span>/ {numPages}</span>
          <i className={`fas fa-step-forward mx-2 ${lastPageClass}`} onClick={goToNextPage} />
        </div>
        {!isTeacher && <i className={`fas fa-chalkboard-teacher mx-2 ${followClass}`} onClick={onFollow} />}
        <div className="d-flex flex-row align-items-center">
          <i className={`fas fa-search-minus mx-2 ${zoomOutClass}`} onClick={zoomOut} />
          <span>{(scale * 100).toFixed()}%</span>
          <i className={`fas fa-search-plus mx-2 ${zoomInClass}`} onClick={zoomIn} />
        </div>
        <div className="d-flex flex-row align-items-center">
          {downloadSlides && <i className="fas fa-download mx-2 clickable" onClick={downloadSlides} />}
          <i
            className={`fas fa-${expanded ? "compress" : "expand"} mx-2 expand`}
            onClick={() => setExpanded((e) => !e)}
          />
        </div>
      </Controls>
    </SSlides>
  );
};

export default PDFReader;
